import { createApp } from 'vue'
import PrimeVue from 'primevue/config';
import App from './App.vue'
import router from './router';
import store from './store';
import VueCookies from 'vue-cookies';
import i18n from './utils/i18n.js';
import mitt from 'mitt'

import './assets/fontawesome/css/all.min.css';
import { definePreset } from '@primevue/themes';
import './style.css'

import Lara from '@primevue/themes/lara';
import ConfirmationService from 'primevue/confirmationservice';
import LoadingSpinner from "./components/LoadingSpinner.vue";
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';

import { registerSW } from 'virtual:pwa-register'
import TitleWithReturnButton from "./components/TitleWithReturnButton.vue";
import * as Sentry from "@sentry/vue";
import api, { refreshToken } from './utils/api.js';

let serviceWorkerUpdateTimeoutId = null;
let serviceWorkerUpdateInProgress = false;
const trackedServiceWorkers = new WeakSet();

const markServiceWorkerUpdating = () => {
    serviceWorkerUpdateInProgress = true;
    store.commit('SET_SERVICE_WORKER_UPDATING', true);

    if (serviceWorkerUpdateTimeoutId !== null) {
        globalThis.clearTimeout(serviceWorkerUpdateTimeoutId);
    }

    serviceWorkerUpdateTimeoutId = globalThis.setTimeout(() => {
        serviceWorkerUpdateInProgress = false;
        store.commit('SET_SERVICE_WORKER_UPDATING', false);
        serviceWorkerUpdateTimeoutId = null;
    }, 10000);
};

const trackInstallingServiceWorker = (serviceWorker) => {
    if (!serviceWorker || trackedServiceWorkers.has(serviceWorker)) {
        return;
    }

    trackedServiceWorkers.add(serviceWorker);

    serviceWorker.addEventListener('statechange', () => {
        if (serviceWorker.state === 'installed' && navigator.serviceWorker.controller) {
            markServiceWorkerUpdating();
        }
    });
};

registerSW({
    immediate: true,
    onRegisteredSW(_, registration) {
        if (!registration) {
            return;
        }

        trackInstallingServiceWorker(registration.installing);

        registration.addEventListener('updatefound', () => {
            trackInstallingServiceWorker(registration.installing);
        });
    },
})

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!serviceWorkerUpdateInProgress) {
            return;
        }

        markServiceWorkerUpdating();
    });
}

globalThis.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    store.commit('SET_INSTALL_PROMPT', event);
    store.commit('SET_INSTALL_PROMPT_ELIGIBLE', true);
});

globalThis.addEventListener('appinstalled', () => {
    store.commit('SET_INSTALL_PROMPT', null);
    store.commit('SET_INSTALL_PROMPT_ELIGIBLE', false);
});

const app = createApp(App);

if (! import.meta.env.DEV) {
    Sentry.init({
        app,
        dsn: 'https://5a87cab8b56b41d98366041d6c1c844d@sentry.wabberserver.nl/74',
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 1.0,
    });
}

const MyPreset = definePreset(Lara, {
    semantic: {
        primary: {
            50: '#e3f6f6',
            100: '#c7eded',
            200: '#9fdddd',
            300: '#73caca',
            400: '#3fb3b3',
            500: '#1a8b8f',
            600: '#16767a',
            700: '#126064',
            800: '#0e4a4d',
            900: '#0a3436',
            950: '#051f20'
        },
        colorScheme: {
            dark: {
                primary: {
                    color: '{primary.500}',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}'
                }
            }
        }
    },
});

app.use(PrimeVue, {
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.dark',
            cssLayer: {
                name: 'primevue',
                order: 'theme, base, primevue'
            },
        }
    }
});

app.use(store);
app.use(VueCookies, { expires: '7d' })

const emitter = mitt();

app.directive('tooltip', Tooltip);

app.config.globalProperties.$emitter = emitter;
app.use(ToastService);
app.use(ConfirmationService);
app.use(i18n);

app.component(LoadingSpinner);
app.component(TitleWithReturnButton);

const bootstrapAuth = async () => {
    const currentPath = globalThis.location.pathname;
    const isGuestPath = [
        '/login',
        '/password-forgotten',
        '/reset-password',
    ].includes(currentPath);

    let token = store.getters['session/accessToken'];

    if (!token) {
        try {
            token = await refreshToken();
        } catch {
            token = null;
        }
    }

    if (!token) {
        await store.dispatch('clearStore');

        if (!isGuestPath) {
            globalThis.location.href = '/login';
        }

        return;
    }

    try {
        const response = await api.get('/me');
        store.commit('SET_USER', response.data.user);
        await store.dispatch('session/initSession');
    } catch {
        await store.dispatch('clearStore');

        if (!isGuestPath) {
            globalThis.location.href = '/login';
        }
    }
};

const startApp = async () => {
    await bootstrapAuth();
    app.use(router);
    await router.isReady();
    app.mount('#app');
};

startApp();
